import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

// material-ui
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import SupplyRequest from '../dataObjects/SupplyRequest';

interface Props {
    request?: SupplyRequest;
    closeDialog: () => void;
    markOrdered: (request: SupplyRequest, dateOrdered: string, adminNote: string) => void;
    closeWithoutOrdering: (request: SupplyRequest, adminNote: string) => void;
}

/**
 * Supply admin dialog: mark a request ordered (with a date), or close it without
 * ordering - the note is required then, so the requester is told why.
 */
const CloseRequestDialog = (props: Props): JSX.Element => {

    const [dateOrdered, setDateOrdered] = useState(dayjs().format('YYYY-MM-DD'));
    const [adminNote, setAdminNote] = useState('');

    useEffect(() => {
        setDateOrdered(dayjs().format('YYYY-MM-DD'));
        setAdminNote(props.request?.adminNote ?? '');
    }, [props.request]);

    return (
        <Dialog open={props.request !== undefined} onClose={props.closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>Close Supply Request</DialogTitle>
            <DialogContent>
                {
                    props.request &&
                    <Stack spacing={2} marginTop={1}>
                        <Typography variant="body2" color="text.secondary">
                            {props.request.requesterName}, {dayjs(props.request.requestDate).format('M/D/YYYY')}
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{props.request.requestText}</Typography>
                        <TextField
                            label="Date ordered"
                            type="date"
                            size="small"
                            value={dateOrdered}
                            onChange={(e) => setDateOrdered(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ maxWidth: 200 }}
                        />
                        <TextField
                            label="Note (optional when ordered, required when not)"
                            multiline
                            minRows={2}
                            size="small"
                            inputProps={{ maxLength: 500 }}
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                        />
                    </Stack>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={props.closeDialog}>Cancel</Button>
                <Button
                    color="warning"
                    disabled={adminNote.trim() === ''}
                    onClick={() => props.request && props.closeWithoutOrdering(props.request, adminNote)}
                >
                    Close without ordering
                </Button>
                <Button
                    variant="contained"
                    disabled={dateOrdered === ''}
                    onClick={() => props.request && props.markOrdered(props.request, dateOrdered, adminNote)}
                >
                    Mark ordered
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CloseRequestDialog;
